import { oc } from "@orpc/contract";
import { z } from "zod";
import { AccountRoleSchema, AdminCapabilitySchema } from "../identity.js";
import { LocaleSchema, SiteSchema } from "../sites.js";
import { ApiAccessSchema } from "./access.js";
export const PrincipalSummary = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    email: z.string().nullable(),
    emailVerified: z.boolean(),
});
export const AccountSummary = z.object({
    id: z.string().uuid(),
    role: AccountRoleSchema,
    preferredLocale: LocaleSchema,
});
/**
 * A bearer session (V6 plan 13.3.1, decision D40): what `identity.exchange` answers with
 * `delivery: "bearer"` for a front-end on another origin, sent back as
 * `Authorization: Bearer v5s_…`. It is `v5s_` plus 43 base64url characters, stored only as its
 * sha256 like a cookie session, lives 24 hours (never extended), and ends at `identity.logout`
 * or at another session's `identity.revokeOtherSessions`.
 */
export const BEARER_SESSION_PREFIX = "v5s_";
export const BEARER_SESSION_TOKEN = /^v5s_[A-Za-z0-9_-]{43}$/;
export const BEARER_SESSION_TTL_HOURS = 24;
/**
 * The one refusal a bearer session meets (401): a `v5s_` token that is unknown, expired,
 * revoked, malformed, or whose account is not active. Drop the token and exchange again.
 */
export const SESSION_INVALID = "session_invalid";
export const BearerSession = z.object({
    token: z.string().regex(BEARER_SESSION_TOKEN),
    expiresAt: z.string().datetime(),
});
/** How `identity.exchange` hands the session over: the cookie (the default), or a bearer token. */
export const SessionDeliverySchema = z.enum(["cookie", "bearer"]);
/**
 * What the caller holds (V6 plan 13.3.1): the session cookie, a bearer session
 * (`Authorization: Bearer v5s_…`), or an account API key with the access it was issued and the
 * one project it is held to (null = the whole account).
 */
export const BootstrapCredential = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("cookie") }),
    z.object({ kind: z.literal("session") }),
    z.object({
        kind: z.literal("api_key"),
        keyId: z.string().uuid(),
        access: ApiAccessSchema,
        projectId: z.string().uuid().nullable(),
    }),
]);
// What a client needs to render for the current caller: the site itself and, when the
// session cookie, a bearer session or an account key is live, who is signed in and what the
// caller holds. Signed out, the four nullable fields are null and `authenticated` is false.
export const Bootstrap = z.object({
    site: SiteSchema,
    authenticated: z.boolean(),
    principal: PrincipalSummary.nullable(),
    account: AccountSummary.nullable(),
    admin: z.object({ capabilities: z.array(AdminCapabilitySchema) }).nullable(),
    credential: BootstrapCredential.nullable(),
});
/** The exchange's answer: the new bootstrap, plus the bearer session when one was asked for. */
export const ExchangeResult = Bootstrap.extend({ session: BearerSession.optional() });
export const identity = {
    bootstrap: oc
        .route({
        summary: "Who is signed in on this site",
        description: "The site record and, when the session cookie, a bearer session or an account API key is live, the principal, the account, the admin surface and the credential the caller holds (the cookie, the bearer session, or the key with its access and project); signed out, those four are null and authenticated is false.",
    })
        .meta({ public: true, access: "read" })
        .output(Bootstrap),
    // A Firebase ID token becomes a session: the cookie, or a bearer token for a front-end on
    // another origin (D40); the response is the new bootstrap.
    exchange: oc
        .route({
        summary: "Turn a Firebase ID token into a session",
        description: 'Verifies the token against the site\'s Firebase project and answers the new bootstrap. By default it mints the per-host session cookie; with delivery "bearer" it sets no cookie and answers session: {token: "v5s_…", expiresAt}, a 24-hour bearer session sent back as Authorization: Bearer. Rate-limited per client address.',
    })
        .meta({ public: true, sessionChanging: true, access: "write", keys: false })
        .input(z.object({
        idToken: z.string().min(1),
        locale: LocaleSchema.optional(),
        delivery: SessionDeliverySchema.optional(),
    }))
        .output(ExchangeResult),
    // Revokes the caller's session row and clears the cookie; always succeeds for the browser.
    logout: oc
        .route({
        summary: "Revoke the session and clear the cookie",
        description: "Revokes the session row behind the cookie and clears it, or, called with a bearer session, revokes that token and leaves any cookie alone; always succeeds for the browser, even without a live session.",
    })
        .meta({ public: true, sessionChanging: true, access: "write", keys: false })
        .output(z.null()),
    // Post-password-reset containment: every OTHER live session of this identity, on both
    // sites, is revoked; the caller's own session survives.
    revokeOtherSessions: oc
        .route({
        summary: "Revoke every other session of this identity",
        description: "Post-password-reset containment: every other live session of the signed-in identity, cookie or bearer, on both sites, is revoked and counted; the caller's own session survives. Needs a session.",
    })
        .meta({ access: "write", keys: false })
        .output(z.object({ revokedSessions: z.number().int().nonnegative() })),
    // Vouches for a Firebase action-link continueUrl: null means "fall back to /login".
    validateContinuation: oc
        .route({
        summary: "Vouch for a Firebase action-link continueUrl",
        description: "Answers the URL when it points at one of this deployment's own hosts, otherwise null, which the app treats as 'fall back to /login'.",
    })
        .meta({ public: true, access: "read" })
        .input(z.object({ continueUrl: z.string().min(1).max(2048) }))
        .output(z.object({ continueUrl: z.string().nullable() })),
    updatePreferences: oc
        .route({
        summary: "Save the account's preferred locale",
        description: "Writes the locale on the signed-in account; the next bootstrap reports it as preferredLocale. Needs a session.",
    })
        .meta({ access: "write", keys: false })
        .input(z.object({ locale: LocaleSchema }))
        .output(z.object({ locale: LocaleSchema })),
};
//# sourceMappingURL=identity.js.map